import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Globe, Gauge, Tag, ArrowRight, Info, TrendingUp, Layers, Heart, Sparkles } from "lucide-react";
import FoundingBadge from "../components/FoundingBadge";
import SiteOwnership from "../components/SiteOwnership";
import SiteListing from "../components/SiteListing";
import AdultToggle from "../components/AdultToggle";
import { UpgradeBanner } from "../components/UpgradeCTA";
import { ReputationBadge } from "../components/ReputationBadge";

function fmtTraffic(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${n}`;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [niches, setNiches] = useState([]);
  const [url, setUrl] = useState(() => user?.site?.url || "");
  const [niche, setNiche] = useState(() => user?.site?.niche || "");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/niches").then(({ data }) => setNiches(data.niches)).catch(() => {});
  }, []);

  // Plan selection happens right after signup — bounce un-chosen users to pricing.
  useEffect(() => {
    if (user && !user.plan_chosen) nav("/pricing");
  }, [user, nav]);

  const site = user?.site;

  // Site was captured at signup (url + niche) but still needs a description — prefill the form.
  useEffect(() => {
    if (site && !site.description) {
      setUrl((u) => u || site.url || "");
      setNiche((n) => n || site.niche || "");
    }
  }, [site]);

  const hasCompleteSite = !!(site && site.description);

  const submit = async (e) => {
    e.preventDefault();
    if (!niche) { toast.error("Please pick a niche"); return; }
    if (description.trim().length < 20) {
      toast.error("Add a description of at least 20 characters — the AI uses it to write on-topic articles.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/site", { url, niche, description });
      await refresh();
      toast.success("Site added — now verify you own it to enter the pool.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verified = !!site?.owner_verified;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground uppercase">Dashboard</div>
            <h1 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight mt-1">
              {user?.name ? `Hey, ${user.name.split(" ")[0]}` : "Your site"}
            </h1>
          </div>
          {user?.founding_member && <FoundingBadge size={16} label />}
        </div>
        {site && verified && (
          <Button onClick={() => nav("/feed")} data-testid="go-to-pool"
            className="rounded-full bg-primary text-white hover:bg-blue-600 glow-primary active:scale-95 transition-transform px-6">
            Enter the pool <ArrowRight className="ml-2" size={16} />
          </Button>
        )}
      </div>

      <UpgradeBanner />

      {hasCompleteSite ? (
        <>
          {!verified && (
            <div className="mt-6 space-y-4" data-testid="ownership-section">
              <SiteOwnership site={site} onVerified={refresh} />
              <AdultToggle site={site} onChange={refresh} />
            </div>
          )}
          {verified && (
            <div className="mt-6 space-y-4">
              <SiteListing site={site} onChange={refresh} />
              <AdultToggle site={site} onChange={refresh} />
            </div>
          )}
          {/* Dense metrics grid */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Profile */}
            <div className="md:col-span-2 rounded-xl border border-white/10 bg-zinc-900/60 overflow-hidden flex">
              <div className="w-32 shrink-0 bg-zinc-800 relative">
                {site.image && <img src={site.image} alt={site.url} className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-zinc-900/60" />
              </div>
              <div className="p-5 min-w-0 flex-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Globe size={14} className="text-primary shrink-0" />
                  <span className="font-mono text-sm truncate" data-testid="metric-url">{site.url}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 mt-2 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 text-xs font-medium">
                  <Tag size={11} /> {site.niche}
                </span>
                {user?.reputation && <ReputationBadge rep={user.reputation} className="ml-2 align-middle" />}
                <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed line-clamp-2">{site.description}</p>
              </div>
            </div>
            <Metric icon={Gauge} label="Domain Authority" value={site.da} testid="metric-da"
              badge={site.mocked ? "PLACEHOLDER" : "MOZ · LIVE"} big />
            <Metric icon={TrendingUp} label="Monthly visits" value={fmtTraffic(site.traffic)} testid="metric-traffic"
              badge={site.traffic >= 10000 ? "10K+" : "EST."} />
          </div>

          {/* Action band — fills space with useful next steps */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <ActionCard icon={Layers} title="The Pool" desc="Swipe DA-matched sites in your niche." onClick={() => nav("/feed")} accent="primary" testid="dash-open-pool" />
            <ActionCard icon={Heart} title="Your matches" desc="Start swaps & track the 48h window." onClick={() => nav("/matches")} accent="rose" testid="dash-open-matches" />
            <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-5">
              <div className="flex items-center gap-2 text-primary"><Info size={15} /><span className="font-mono text-[11px] tracking-[0.15em] uppercase">How it works</span></div>
              <ul className="mt-3 space-y-1.5 text-[13px] text-muted-foreground">
                <li className="flex gap-2"><span className="text-primary font-mono">01</span> Same-niche sites only.</li>
                <li className="flex gap-2"><span className="text-primary font-mono">02</span> Within ±10 of your DA.</li>
                <li className="flex gap-2"><span className="text-primary font-mono">03</span> Mutual likes become matches.</li>
                <li className="flex gap-2"><span className="text-primary font-mono">04</span> Publish, paste URL, verify.</li>
              </ul>
            </div>
          </div>
        </>
      ) : (
        <div className="mt-6 grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-xl border border-white/10 bg-zinc-900/60 p-7">
            <h2 className="font-serif font-semibold text-2xl tracking-tight">{site ? "Complete your site profile" : "Add your website"}</h2>
            <p className="text-muted-foreground text-sm mt-1.5 leading-relaxed">
              {site
                ? "Almost there — add a description so the AI can write on-topic guest posts, then verify ownership to enter the pool."
                : "We'll fetch your Domain Authority from Moz and match you with sites in your niche within ±10 DA points."}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="url">Website URL</Label>
                <Input id="url" required value={url} onChange={(e) => setUrl(e.target.value)}
                  data-testid="site-url-input" className="rounded-lg bg-zinc-950 border-white/10 h-11 font-mono focus-visible:ring-primary" placeholder="https://yoursite.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Primary niche</Label>
                <Select key={niches.length ? "loaded" : "loading"} value={niche} onValueChange={setNiche}>
                  <SelectTrigger data-testid="site-niche-select" className="rounded-lg bg-zinc-950 border-white/10 h-11">
                    <SelectValue placeholder="Select a niche" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    {niches.map((n) => (<SelectItem key={n} value={n} data-testid={`niche-option-${n}`}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="desc">Site description <span className="text-primary">*</span></Label>
                <Textarea id="desc" required minLength={20} value={description} onChange={(e) => setDescription(e.target.value)}
                  data-testid="site-description-input" className="rounded-lg bg-zinc-950 border-white/10 min-h-[88px] focus-visible:ring-primary"
                  placeholder="Describe what your site does and who it's for. The AI uses this to write on-topic guest posts." />
                <p className="text-[11px] text-muted-foreground">
                  {description.trim().length < 20
                    ? `At least 20 characters (${description.trim().length}/20). The more specific, the better your matched articles.`
                    : "Great — specific descriptions produce far more relevant articles."}
                </p>
              </div>
              <Button type="submit" disabled={busy} data-testid="site-submit"
                className="w-full rounded-full bg-primary text-white hover:bg-blue-600 h-11 glow-primary active:scale-95 transition-transform">
                {busy ? "Fetching DA…" : "Verify & fetch DA"}
              </Button>
            </form>
          </div>
          <div className="lg:col-span-2 rounded-xl border border-white/10 bg-zinc-900/60 p-7">
            <div className="flex items-center gap-2 text-primary"><Sparkles size={16} /><span className="font-mono text-[11px] tracking-[0.15em] uppercase">How matching works</span></div>
            <ul className="mt-5 space-y-4 text-sm text-muted-foreground">
              <li className="flex gap-3"><span className="text-primary font-mono">01</span> Only same-niche sites appear in your pool.</li>
              <li className="flex gap-3"><span className="text-primary font-mono">02</span> Only sites within ±10 of your DA are shown.</li>
              <li className="flex gap-3"><span className="text-primary font-mono">03</span> Mutual likes become a Match with an AI studio.</li>
              <li className="flex gap-3"><span className="text-primary font-mono">04</span> Publish your post, paste the URL, get verified.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid, badge, big }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-white/10 bg-zinc-900/60 p-5 flex flex-col justify-between min-h-[120px]">
      <div className="flex items-center justify-between">
        <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/20 flex items-center justify-center"><Icon size={17} className="text-primary" /></div>
        {badge && <span className="font-mono text-[9px] tracking-widest text-muted-foreground rounded-md border border-white/10 px-2 py-0.5">{badge}</span>}
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground uppercase mt-3">{label}</div>
        <div data-testid={testid} className={`mt-0.5 font-mono font-bold tracking-tight ${big ? "text-4xl text-primary" : "text-3xl text-foreground"}`}>{value}</div>
      </div>
    </motion.div>
  );
}

function ActionCard({ icon: Icon, title, desc, onClick, accent, testid }) {
  const tone = accent === "rose" ? "text-rose-400 bg-rose-500/10 border-rose-500/20" : "text-primary bg-primary/15 border-primary/20";
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left rounded-xl border border-white/10 bg-zinc-900/60 p-5 hover:bg-zinc-900 hover:-translate-y-0.5 transition-all group">
      <div className={`h-9 w-9 rounded-lg border flex items-center justify-center ${tone}`}><Icon size={17} /></div>
      <div className="mt-3 font-serif font-semibold text-lg flex items-center gap-1.5">{title}<ArrowRight size={15} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-muted-foreground" /></div>
      <p className="text-[13px] text-muted-foreground mt-0.5">{desc}</p>
    </button>
  );
}
